import {
  SkyButton,
  SkyCard,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import { failureMessage } from "@/lib/api";
import { describeFailure } from "@/lib/feedback/failure";
import { FailureDetails } from "@/lib/feedback/InlineErrorBar";

/**
 * The four states every screen in this package owes the reader — loading, empty,
 * error, denied — written once (UX-1, canvases 07/08/09 block "Loading, empty,
 * error, denied").
 *
 * It lives under `components/access/` because packages own whole directories in
 * this session and `access`, `settings` and `audit` are all this package's; a
 * fifth shared directory would belong to nobody and two agents would create it.
 *
 * The empty state always teaches the next action rather than reporting that
 * there is nothing here, and the error state always carries the reference id,
 * because "it failed" without an id starts every support conversation with
 * "roughly when?".
 */

export function LoadingRows({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <SkySkeleton testId="loading-rows" aria-busy="true" aria-live="polite" className="space-y-2">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex gap-3">
          {Array.from({ length: columns }, (_, column) => (
            <SkySkeletonItem key={column} size={18} className="flex-1" />
          ))}
        </div>
      ))}
    </SkySkeleton>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <SkyCard appearance="outline" testId="empty-state">
      <div className="flex flex-col items-start gap-2 p-2">
        <SkyText as="h2" size={400} weight="semibold">
          {title}
        </SkyText>
        <SkyText as="p" size={300} color="muted">
          {message}
        </SkyText>
        {action ? <div className="pt-2">{action}</div> : null}
      </div>
    </SkyCard>
  );
}

/** The reference id is the correlation id; `failureMessage` puts it on the end exactly once. */
export function ErrorState({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry?: () => void;
}) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title={title}
      testId="error-state"
      message={
        <span>
          {failureMessage(error, "Something went wrong while loading this screen.")}
          {/* B-23 (round 9): the server's per-field reasons, which nothing on this screen maps. */}
          <FailureDetails details={describeFailure(error).details} testId="error-state-details" />
        </span>
      }
      actions={
        onRetry ? (
          <SkyButton appearance="secondary" size="small" onClick={onRetry}>
            Try again
          </SkyButton>
        ) : undefined
      }
    />
  );
}

/**
 * A control the signed-in user may not use (FR-13).
 *
 * It names the permission so an administrator can grant exactly that, and it is
 * shown rather than hidden wherever the reader would otherwise wonder where the
 * screen went — hiding is a courtesy, not the control.
 */
export function PermissionNotice({
  permission,
  message,
  title = "You do not have access to this screen",
}: {
  permission: string;
  message?: string;
  title?: string;
}) {
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      title={title}
      testId="permission-denied"
      message={
        message ??
        `This screen needs the ${permission} permission in this instance. Ask an administrator to grant it.`
      }
    />
  );
}
